import { yupResolver } from '@hookform/resolvers/yup';
import { ALL_LNGS_NAMESPACES, LNGS_NAMESPACE_MODULES } from '@ingenico/util-i18n-config';
import type { ArtifactCreationBody, ArtifactSimpleResource } from '@ingenico/react-form-builder';
import type { RenderSmartFieldDataFileInputParams } from '@ingenico/react-smartfield-utils/smartfield-ui';
import type { RenderFieldProps } from '@ingenico/react-section-utils';
import type { FileWithStatus } from '@ingenico/react-ui';
import { FileStatus } from '@ingenico/react-ui/file-handler';
import type { KebabMenuOption } from '@ingenico/react-ui/kebab-menu';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { toString } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { mixed, object, string } from 'yup';

interface UseParameterModuleSmartFieldDataFileInputParams {
  readonly config: RenderSmartFieldDataFileInputParams['config'];
  readonly artifacts: ArtifactSimpleResource[];
  readonly artifactsIsLoading: boolean;
  readonly onArtifactSearch: (query: string, acceptedExtensions?: readonly string[]) => void;
  readonly onArtifactCreate: (
    payload: ArtifactCreationBody,
    onSuccess?: (artifact: ArtifactSimpleResource) => void,
    onError?: () => void,
  ) => void;
  readonly onArtifactPreview: (id: string) => void;
}

export function useParameterModuleSmartFieldDataFileInput({
  config,
  artifacts,
  artifactsIsLoading,
  onArtifactSearch,
  onArtifactCreate,
  onArtifactPreview,
}: UseParameterModuleSmartFieldDataFileInputParams) {
  const { t } = useTranslation([LNGS_NAMESPACE_MODULES.COMMON, LNGS_NAMESPACE_MODULES.DEFAULT]);
  const inputConfiguration = config.inputCustomConfig;
  const [createdArtifacts, setCreatedArtifacts] = useState<ArtifactSimpleResource[]>([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const [fileToUpload, setFileToUpload] = useState<FileWithStatus | null>(null);

  const uploadSchema = useMemo(
    () =>
      object({
        file: mixed<File>().required(t('error.required', { ns: ALL_LNGS_NAMESPACES.COMMON })),
        checksum: string().default(''),
        description: string().default(''),
        name: string().required(t('error.required', { ns: ALL_LNGS_NAMESPACES.COMMON })),
      }).required(),
    [t],
  );
  const uploadForm = useForm<ArtifactCreationBody>({ mode: 'onChange', resolver: yupResolver(uploadSchema) });

  const mergedArtifacts = useMemo(() => {
    const artifactsById = new Map<string, ArtifactSimpleResource>();
    for (const artifact of [...artifacts, ...createdArtifacts]) artifactsById.set(artifact.id, artifact);
    return [...artifactsById.values()];
  }, [artifacts, createdArtifacts]);

  const fileRules = useMemo(
    () => ({
      fileSize: inputConfiguration.maxSize ? inputConfiguration.maxSize / 1_000_000 : undefined,
      fileTypes: inputConfiguration.acceptedExtensions?.map((extension) =>
        extension.startsWith('.') ? extension : `.${extension}`,
      ),
    }),
    [inputConfiguration.acceptedExtensions, inputConfiguration.maxSize],
  );

  const onUploadModalClose = useCallback(() => {
    setIsUploadModalOpen(false);
    setFileToUpload(null);
    uploadForm.reset();
  }, [uploadForm]);

  const onChangeUploadFiles = useCallback(
    (fieldProps: RenderFieldProps<ArtifactCreationBody, 'file'>, files: FileWithStatus[]) => {
      const [file] = files;
      if (file?.file) {
        fieldProps.field.onChange(file.file);
        uploadForm.setValue('checksum', file.hash);
        setFileToUpload({ ...file, status: FileStatus.UPLOADED });
      } else {
        fieldProps.field.onChange(undefined);
        uploadForm.setValue('checksum', '');
        setFileToUpload(null);
      }
    },
    [uploadForm],
  );

  const onUpload = useCallback(
    (onChange: (value: string | null) => void) => {
      setIsUploadLoading(true);
      onArtifactCreate(
        uploadForm.getValues(),
        (createdArtifact) => {
          setCreatedArtifacts((current) => [...current, createdArtifact]);
          onChange(createdArtifact.id);
          setIsUploadLoading(false);
          onUploadModalClose();
        },
        () => setIsUploadLoading(false),
      );
    },
    [onArtifactCreate, onUploadModalClose, uploadForm],
  );

  const getKebabOptions = useCallback(
    (value: string | null, onChange: (value: string | null) => void): KebabMenuOption[] => [
      {
        icon: <VisibilityOutlinedIcon fontSize="small" color="primary" />,
        label: toString(t('button.previewFile', { ns: ALL_LNGS_NAMESPACES.COMMON })),
        onClick: () => value && onArtifactPreview(value),
        disabled: !value,
      },
      {
        icon: <FileUploadOutlinedIcon fontSize="small" color="primary" />,
        label: toString(t('button.uploadFile', { ns: ALL_LNGS_NAMESPACES.COMMON })),
        onClick: () => setIsUploadModalOpen(true),
      },
      {
        icon: <RefreshOutlinedIcon fontSize="small" color="primary" />,
        label: toString(t('button.resetValue', { ns: ALL_LNGS_NAMESPACES.MODULES })),
        onClick: () => onChange(null),
        disabled: !value,
      },
    ],
    [onArtifactPreview, t],
  );

  return {
    artifacts: mergedArtifacts,
    artifactsIsLoading,
    fileRules,
    fileToUpload,
    getKebabOptions,
    isRequired: inputConfiguration.isRequired,
    isUploadFormValid: uploadForm.formState.isValid,
    isUploadLoading,
    isUploadModalOpen,
    label: inputConfiguration.label ?? '',
    onArtifactSearch,
    onChangeUploadFiles,
    onUpload,
    onUploadModalClose,
    t,
    uploadControl: uploadForm.control,
  };
}
